import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { HeaderComponent } from '../../header/header.component';
import { FooterComponent } from '../../footer/footer.component';

interface FaqItem {
  question: string;
  answer: string;
  expanded: boolean;
  keywords: string[];
  relevanceScore?: number;
}

@Component({
  selector: 'app-help-center',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    HeaderComponent,
    FooterComponent,
    FormsModule,
  ],
  templateUrl: './help-center.component.html',
  styleUrls: ['./help-center.component.css'],
})
export class HelpCenterComponent implements OnInit {
  searchQuery: string = '';
  selectedTopic: string = '';
  filteredFaqItems: FaqItem[] = [];
  searchMinLength: number = 2;

  popularTopics: string[] = [
    'Account',
    'Payment',
    'Shipping',
    'Returns',
    'Selling',
    'Buying',
    'Security',
    'Mobile',
  ];

  faqItems: FaqItem[] = [
    {
      question: 'What is Markado?',
      answer:
        'Markado is a platform where independent sellers showcase and sell their products. We connect buyers with a wide variety of unique items from different sellers. Markado itself does not sell products, we provide the space and tools for sellers to showcase their own listings.',
      expanded: false,
      keywords: [
        'markado',
        'platform',
        'marketplace',
        'what is',
        'about',
        'sellers',
        'buyers',
      ],
    },
    {
      question: 'How do I create an account?',
      answer:
        'To create an account, simply go to the Register page, fill in your details and you are all set!',
      expanded: false,
      keywords: [
        'account',
        'register',
        'signup',
        'create account',
        'join',
        'registration',
      ],
    },
    {
      question: 'How do I change my account password?',
      answer:
        'To change your account password, go to your profile in the navigation menu, click "My Profile" then click "Edit Password". Enter your current password, then enter and confirm your new password. Click "Save Changes" to update your password. Alternatively, you can request a password reset link to be sent to your email while logged out from Forgot Password page.',
      expanded: false,
      keywords: [
        'account',
        'password',
        'change password',
        'reset password',
        'security',
      ],
    },
    {
      question: 'How do I delete my account?',
      answer:
        'To delete your account, please contact our support team through the “Contact Us” page. Once your request is received, your account and all associated data will be permanently deleted within seven days. Please note that this process is irreversible, as we do not retain any backups or recoverable copies of deleted data.',
      expanded: false,
      keywords: ['account', 'delete account', 'remove account', 'cancel'],
    },
    {
      question: 'How do I sell something?',
      answer:
        'After logging in, click on the "Sell" button in the navigation menu. Fill in all the required details about your item and click "Submit". Your listing will be reviewed and published shortly.',
      expanded: false,
      keywords: [
        'listing',
        'post',
        'sell',
        'selling',
        'create listing',
        'upload',
      ],
    },
    {
      question: 'How do payments work and what methods are accepted?',
      answer:
        'Markado supports various payment methods including credit/debit cards, bank transfers and mobile payments. You can top up your balance using these options. All transactions are processed securely.',
      expanded: false,
      keywords: [
        'payment',
        'payment methods',
        'pay',
        'transaction',
        'money',
        'purchase',
        'credit card',
        'debit card',
        'bank transfer',
        'top up',
        'phone payment',
      ],
    },
    {
      question: 'What is your return policy?',
      answer:
        'Because our platform features products listed by individual sellers, return policies may vary. We recommend checking the specific sellers return terms on their product listing. If you need to request a return, please contact the seller directly to discuss options. Most sellers are happy to work with you to find a solution.',
      expanded: false,
      keywords: ['return', 'refund', 'send back', 'exchange', 'policy'],
    },
    {
      question: 'Does Markado support Jobs?',
      answer:
        'Yes, Markado supports listing job opportunities alongside physical products. When creating a listing, simply select “Jobs” as the category and provide detailed information about the position you’re offering or the type of work you’re seeking.',
      expanded: false,
      keywords: ['service', 'sell', 'selling', 'listing', 'offer'],
    },
    {
      question: 'How do I contact a seller?',
      answer:
        'To contact a seller, navigate to their listing and click on the "Contact Seller" button. This will open a messaging interface where you can ask questions. All communications are kept within our platform for security and record-keeping.',
      expanded: false,
      keywords: ['contact', 'seller', 'message', 'communication', 'chat'],
    },
    {
      question: 'Is there a mobile app available?',
      answer:
        'We are currently working on mobile apps for both iOS and Android. Coming soon to the App Store and Google Play Store, so you can browse, buy, and sell on the go!',
      expanded: false,
      keywords: ['mobile', 'app', 'phone', 'android', 'ios', 'application'],
    },
  ];

  constructor(private router: Router) {}

  ngOnInit(): void {
    this.filteredFaqItems = [...this.faqItems];
  }

  toggleFaq(item: FaqItem): void {
    this.faqItems.forEach((faq) => {
      if (faq !== item) {
        faq.expanded = false;
      }
    });
    this.filteredFaqItems.forEach((faq) => {
      if (faq !== item) {
        faq.expanded = false;
      }
    });
    item.expanded = !item.expanded;
  }

  searchFaq(): void {
    this.selectedTopic = '';
    this.closeAllFaqs();

    if (
      !this.searchQuery ||
      this.searchQuery.trim().length < this.searchMinLength
    ) {
      this.filteredFaqItems = [...this.faqItems];
      return;
    }

    const terms = this.normalizeSearchTerms(this.searchQuery);
    if (terms.length === 0) {
      this.filteredFaqItems = [...this.faqItems];
      return;
    }

    this.filteredFaqItems = this.faqItems
      .map((item) => {
        const score = this.calculateRelevanceScore(item, terms);
        return { ...item, relevanceScore: score };
      })
      .filter((item) => (item.relevanceScore ?? 0) > 0)
      .sort((a, b) => (b.relevanceScore ?? 0) - (a.relevanceScore ?? 0));

    if (this.filteredFaqItems.length > 0 && this.filteredFaqItems.length <= 3) {
      this.filteredFaqItems[0].expanded = true;
    }
  }

  private normalizeSearchTerms(query: string): string[] {
    return query
      .toLowerCase()
      .trim()
      .replace(/[^\w\s]/gi, ' ')
      .split(/\s+/)
      .filter((term) => term.length >= this.searchMinLength);
  }

  private calculateRelevanceScore(
    item: FaqItem,
    searchTerms: string[]
  ): number {
    let score = 0;
    const questionLower = item.question.toLowerCase();
    const answerLower = item.answer.toLowerCase();

    for (const term of searchTerms) {
      if (questionLower.includes(term)) {
        if (this.hasExactWordMatch(questionLower, term)) {
          score += 10;
        } else {
          score += 5;
        }
      }

      const keywordMatch = item.keywords.some((keyword) => {
        const keywordLower = keyword.toLowerCase();
        return keywordLower.includes(term) || term.includes(keywordLower);
      });

      if (keywordMatch) {
        score += 3;
      }

      if (answerLower.includes(term)) {
        score += 1;
      }
    }

    return score;
  }

  private hasExactWordMatch(text: string, term: string): boolean {
    const regex = new RegExp(`\\b${term}\\b`, 'i');
    return regex.test(text);
  }

  searchByTopic(topic: string): void {
    this.searchQuery = '';
    this.closeAllFaqs();

    if (this.selectedTopic === topic) {
      this.selectedTopic = '';
      this.filteredFaqItems = [...this.faqItems];
      return;
    }

    this.selectedTopic = topic;
    const topicLower = topic.toLowerCase();

    this.filteredFaqItems = this.faqItems
      .map((item) => {
        let score = 0;

        if (item.question.toLowerCase().includes(topicLower)) {
          score += 5;
        }

        const keywordMatch = item.keywords.some(
          (keyword) =>
            keyword.toLowerCase().includes(topicLower) ||
            topicLower.includes(keyword.toLowerCase())
        );

        if (keywordMatch) {
          score += 3;
        }

        if (item.answer.toLowerCase().includes(topicLower)) {
          score += 1;
        }

        return { ...item, relevanceScore: score };
      })
      .filter((item) => (item.relevanceScore ?? 0) > 0)
      .sort((a, b) => (b.relevanceScore ?? 0) - (a.relevanceScore ?? 0));

    if (this.filteredFaqItems.length > 0 && this.filteredFaqItems.length <= 3) {
      this.filteredFaqItems[0].expanded = true;
    }
  }

  private closeAllFaqs(): void {
    this.faqItems.forEach((item) => {
      item.expanded = false;
    });
  }

  resetSearch(): void {
    this.searchQuery = '';
    this.selectedTopic = '';
    this.filteredFaqItems = [...this.faqItems];
    this.closeAllFaqs();
  }

  navigateWithScroll(route: string): void {
    this.router.navigateByUrl(route).then(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }
}
